// src/components/GearingCalculator.js
import React, { useState, useEffect, useRef } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase';
import { useAuthState } from 'react-firebase-hooks/auth';
import '../styles.css';

function GearingCalculator({ params, sessionId, vehicleId, eventId }) {
  const [user, loading, error] = useAuthState(auth);
  const [showFullScreen, setShowFullScreen] = useState(false);
  const [vehicleName, setVehicleName] = useState('Unnamed Vehicle');
  const [eventName, setEventName] = useState('Unnamed Event');
  const [sessionName, setSessionName] = useState('Unnamed Session');
  const [syncStatus, setSyncStatus] = useState('Loading...');
  const svgRef = useRef(null);

  // Fetch vehicle, event, and session names
  useEffect(() => {
    const loadData = async () => {
      if (!user || !vehicleId || !eventId) {
        console.warn('Missing user, vehicleId, or eventId for GearingCalculator');
        setSyncStatus('Error: Missing data');
        return;
      }
      try {
        setSyncStatus('Loading...');
        let sessionData = null, vehicleData = null, eventData = null;
        // Fetch session (if sessionId exists)
        if (sessionId) {
          const sessionDoc = await getDoc(doc(db, `users/${user.uid}/sessions/${sessionId}`));
          sessionData = sessionDoc.exists() ? sessionDoc.data() : null;
        }
        // Fetch vehicle and event
        const vehicleDoc = await getDoc(doc(db, `users/${user.uid}/vehicles/${vehicleId}`));
        vehicleData = vehicleDoc.exists() ? vehicleDoc.data() : null;
        const eventDoc = await getDoc(doc(db, `users/${user.uid}/events/${eventId}`));
        eventData = eventDoc.exists() ? eventDoc.data() : null;

        setSessionName(sessionData?.sessionName || 'Unnamed Session');
        setVehicleName(vehicleData?.name || 'Unnamed Vehicle');
        setEventName(eventData?.name || 'Unnamed Event');
        setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
      } catch (err) {
        console.error('Error fetching data for GearingCalculator:', err.message);
        setSyncStatus(`Error: ${err.message}`);
      }
    };
    if (!loading && user) loadData();

    // Update sync status on connectivity
    const updateSyncStatus = () => {
      setSyncStatus(navigator.onLine ? 'Synced' : 'Offline (cached)');
    };
    window.addEventListener('online', updateSyncStatus);
    window.addEventListener('offline', updateSyncStatus);
    return () => {
      window.removeEventListener('online', updateSyncStatus);
      window.removeEventListener('offline', updateSyncStatus);
    };
  }, [user, loading, sessionId, vehicleId, eventId]);

  // Extract parameters from props
  const driveType = params['Drive Type'] || 'RWD';
  const numGears = parseInt(params['Number of Gears'] || '6');
  const finalDrive = parseFloat(params['Final Drive']) || 4;
  const dropGear = parseFloat(params['Drop Gear']) || 1; // Default to 1 if not present
  const revLimit = parseFloat(params['Rev limit']) || 8000;

  // Helper function to get circumference with compatibility for old and new naming
  const getCircumference = (position) => {
    // Check new naming first: Circumference (Front/Rear)
    let value = params[`Circumference (${position})`];
    if (value !== undefined) return parseFloat(value) || 2000;

    // Check old naming: Front/Rear Circumference
    value = params[`${position} Circumference`];
    if (value !== undefined) return parseFloat(value) || 2000;

    // Default
    return 2000;
  };

  const frontCircumference = getCircumference('Front'); // mm
  const rearCircumference = getCircumference('Rear'); // mm
  const gearRatios = Array.from({ length: numGears }, (_, i) =>
    parseFloat(params[`Gear Ratio ${i + 1}`]) || (5 - i * 0.5)
  );

  // Format gear ratios with correct ordinal suffixes
  const ordinalSuffix = (n) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };
  const gearRatiosText = gearRatios.map((ratio, i) => `${ordinalSuffix(i + 1)}: ${ratio.toFixed(3)}`).join(' ');

  // Calculate effective tire circumference based on drive type
  const effectiveCircumference = driveType === 'AWD'
    ? (frontCircumference + rearCircumference) / 2
    : driveType === 'FWD'
    ? frontCircumference
    : rearCircumference;

  // Constants for calculations
  const MM_TO_M = 0.001; // mm to meters
  const M_TO_KM = 0.001; // meters to km
  const MIN_PER_HOUR = 60; // minutes per hour
  const SVG_WIDTH = 600; // Base width for viewBox
  const SVG_HEIGHT = 500; // Height increased by 100px
  const MARGIN = { top: 40, right: 40, bottom: 60, left: 60 };
  const FULL_SCREEN_MARGIN = { top: 30, right: 20, bottom: 60, left: 60 }; // Extra top margin for text
  const GRAPH_WIDTH = SVG_WIDTH - MARGIN.left - MARGIN.right;
  const GRAPH_HEIGHT = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const FULL_SCREEN_GRAPH_HEIGHT = SVG_HEIGHT - FULL_SCREEN_MARGIN.top - FULL_SCREEN_MARGIN.bottom;

  // Calculate speed for a given RPM and gear
  const calculateSpeed = (rpm, gearRatio) => {
    const axleRpm = rpm / (gearRatio * finalDrive * dropGear);
    const speedMPerMin = axleRpm * effectiveCircumference * MM_TO_M; // meters/min
    const speedKmPerHour = speedMPerMin * MIN_PER_HOUR * M_TO_KM; // km/h
    return speedKmPerHour.toFixed(0); // Round to integer for display
  };

  // Calculate RPM at a given speed for a gear
  const calculateRpm = (speed, gearRatio) => {
    const speedMPerMin = (speed / MIN_PER_HOUR) / M_TO_KM; // km/h to meters/min
    const axleRpm = speedMPerMin / (effectiveCircumference * MM_TO_M);
    const rpm = axleRpm * gearRatio * finalDrive * dropGear;
    return rpm.toFixed(0);
  };

  // Generate gear data
  const gearData = gearRatios.map((ratio, index) => {
    const maxSpeed = calculateSpeed(revLimit, ratio);
    const points = [];
    if (index === 0) {
      points.push({ rpm: 0, speed: 0 });
    } else {
      const prevMaxSpeed = calculateSpeed(revLimit, gearRatios[index - 1]);
      const startRpm = calculateRpm(prevMaxSpeed, ratio);
      points.push({ rpm: parseFloat(startRpm), speed: parseFloat(prevMaxSpeed) });
    }
    points.push({ rpm: revLimit, speed: parseFloat(maxSpeed) });
    return { ratio, points, maxSpeed: parseFloat(maxSpeed) };
  });

  // Determine scales
  const maxRpm = Math.ceil(revLimit / 2000) * 2000; // Round up to next 2000
  const maxSpeed = Math.max(...gearData.map(g => g.maxSpeed), 100); // Minimum 100 km/h
  const xScale = (speed) => (speed / maxSpeed) * GRAPH_WIDTH;
  const yScale = (rpm, height = GRAPH_HEIGHT) => {
    return height - (rpm / maxRpm) * height;
  };

  // Generate Y-axis ticks (every 2000 RPM, up to revLimit + 2000)
  const yTicks = [];
  for (let rpm = 2000; rpm <= maxRpm; rpm += 2000) {
    yTicks.push(rpm);
  }





  // Render graph (shared between normal and full-screen views)
  const renderGraph = (isFullScreen = false) => {
    const margin = isFullScreen ? FULL_SCREEN_MARGIN : MARGIN;
    const graphHeight = isFullScreen ? FULL_SCREEN_GRAPH_HEIGHT : GRAPH_HEIGHT;
    const localYScale = (rpm) => yScale(rpm, graphHeight);

    // Generate SVG paths and labels with offset to prevent overlap
    const paths = gearData.map((gear, index) => {
      const points = gear.points.map(p => `${xScale(p.speed)},${localYScale(p.rpm)}`).join(' L ');
      const pathD = `M ${points}`;
      const prevMaxSpeed = index > 0 ? gearData[index - 1].maxSpeed : 0;
      const shiftRpm = index > 0 ? calculateRpm(prevMaxSpeed, gear.ratio) : null;
      const labelText = index > 0 ? `${prevMaxSpeed} @\n${shiftRpm}` : null;
      const labelX = index > 0 ? xScale(prevMaxSpeed) : null;
      const labelY = index > 0 ? localYScale(shiftRpm) + 20 : null;
      return { pathD, index: index + 1, labelText, labelX, labelY };
    });

    // Adjust label positions to prevent overlap
    const adjustedPaths = paths.map((path, i) => {
      if (!path.labelText) return path;
      let offsetY = path.labelY;
      for (let j = 0; j < i; j++) {
        if (paths[j].labelText && Math.abs(path.labelX - paths[j].labelX) < 30 && Math.abs(offsetY - paths[j].labelY) < 20) {
          offsetY += 20; // Shift down by 20px
        }
      }
      return { ...path, labelY: offsetY };
    });

    return (
      <g transform={`translate(${margin.left},${margin.top})`}>
        {/* Axes */}
        <line
          x1="0"
          y1={graphHeight}
          x2={GRAPH_WIDTH}
          y2={graphHeight}
          stroke="#333"
          strokeWidth="2"
        />
        <line
          x1="0"
          y1="0"
          x2="0"
          y2={graphHeight}
          stroke="#333"
          strokeWidth="2"
        />
        {/* Axis Labels */}
        <text x={GRAPH_WIDTH / 2} y={graphHeight + 40} textAnchor="middle" className="graph-label">
          Speed (km/h)
        </text>
        <text
          x={-graphHeight / 2}
          y={-40}
          textAnchor="middle"
          transform="rotate(-90)"
          className="graph-label"
        >
          RPM (x1000)
        </text>
        {/* Grid Lines */}
        {Array.from({ length: 5 }, (_, i) => (maxSpeed / 4) * i).map((speed, i) => (
          <line
            key={`x-grid-${i}`}
            x1={xScale(speed)}
            y1="0"
            x2={xScale(speed)}
            y2={graphHeight}
            stroke="#ccc"
            strokeDasharray="2,2"
          />
        ))}
        {yTicks.map((rpm, i) => (
          <line
            key={`y-grid-${i}`}
            x1="0"
            y1={localYScale(rpm)}
            x2={GRAPH_WIDTH}
            y2={localYScale(rpm)}
            stroke="#ccc"
            strokeDasharray="2,2"
          />
        ))}
        {/* Rev Limit Line */}
        <line
          x1="0"
          y1={localYScale(revLimit)}
          x2={GRAPH_WIDTH}
          y2={localYScale(revLimit)}
          stroke="red"
          strokeWidth="2"
        />
        {/* Gear Lines and Labels */}
        {adjustedPaths.map((path, i) => (
          <g key={`gear-${path.index}`}>
            <path
              d={path.pathD}
              fill="none"
              stroke={`hsl(${i * 360 / numGears}, 70%, 50%)`}
              strokeWidth="3"
              strokeLinecap="round"
            />
            {i > 0 && (
              <line
                x1={xScale(gearData[i - 1].maxSpeed)}
                y1={localYScale(revLimit)}
                x2={xScale(gearData[i - 1].maxSpeed)}
                y2={localYScale(gearData[i].points[0].rpm)}
                stroke="#333"
                strokeWidth="2"
                strokeDasharray="5,5"
              />
            )}
            {path.labelText && (
              <text
                x={path.labelX}
                y={path.labelY}
                textAnchor="middle"
                className="gear-label"
              >
                {path.labelText.split('\n').map((line, idx) => (
                  <tspan key={idx} x={path.labelX} dy={idx === 0 ? 5 : 22}>{line}</tspan>
                ))}
              </text>
            )}
          </g>
        ))}
        {/* Axis Tick Labels */}
        {Array.from({ length: 5 }, (_, i) => (maxSpeed / 4) * i).map((speed, i) => (
          <text
            key={`x-tick-${i}`}
            x={xScale(speed)}
            y={graphHeight + 20}
            textAnchor="middle"
            className="tick-label"
          >
            {speed.toFixed(0)}
          </text>
        ))}
        {yTicks.map((rpm, i) => (
          <text
            key={`y-tick-${i}`}
            x={-10}
            y={localYScale(rpm) + 5}
            textAnchor="end"
            className="tick-label"
          >
            {(rpm / 1000).toFixed(0)}
          </text>
        ))}
      </g>
    );
  };

  return (
    <>
      <div className="tab-group">
        <div className="tab-group-title-container">
          <h3 className="tab-group-title">Gearing Calculator</h3>
          <button onClick={() => setShowFullScreen(true)} className="graph-action-btn" title="Expand">
            <i className="material-icons">open_in_full</i>
          </button>
        </div>
        <div className="gearing-graph-container">
          <svg
            viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
            preserveAspectRatio="xMidYMid meet"
            className="gearing-graph"
            ref={svgRef}
          >
            {renderGraph()}
          </svg>
        </div>
      </div>
      {showFullScreen && (
        <div className="overlay-card">
          <div className="card-container gearing-calculator-fullscreen">
            <div className="page-title">
              <h2>Gearing Calculator</h2>
              <div>
                <button onClick={() => setShowFullScreen(false)} className="close-button">✕</button>
              </div>
            </div>
            <div className="gearing-info">
              <p><strong>Vehicle:</strong> {vehicleName}</p>
              <p><strong>Event:</strong> {eventName}</p>
              <p><strong>Session:</strong> {sessionName}</p>
              <p><strong>Gear Ratios:</strong> Final: {finalDrive.toFixed(3)}{dropGear !== 1 ? ` Drop: ${dropGear.toFixed(3)}` : ''} {gearRatiosText}</p>
              <p><strong>Tire Circumference:</strong> {driveType === 'AWD' ? `Front: ${frontCircumference} mm, Rear: ${rearCircumference} mm (Avg: ${effectiveCircumference.toFixed(0)} mm)` : `${driveType === 'FWD' ? 'Front' : 'Rear'}: ${effectiveCircumference} mm`}</p>
            </div>
            <div className="gearing-graph-container">
              <svg
                viewBox={`0 0 ${SVG_WIDTH} ${SVG_HEIGHT}`}
                preserveAspectRatio="xMidYMid meet"
                className="gearing-graph"
                ref={svgRef}
              >
                {renderGraph(true)}
              </svg>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default GearingCalculator;